import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { withLocale } from "~/lib/utils/with-locale";
import { useForm } from "react-hook-form";
import z from "zod";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  authClient,
  createAuthError,
  localizeAuthErrorMessage,
} from "~/lib/better-auth/client";
import { ForgotPasswordSchema } from "~/lib/zod-schemas/auth.schema";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/components/ui/form";
import { CheckCircle, LoaderIcon, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";

export const Route = createFileRoute("/_login/forgot-password")({
  component: ForgotPasswordPage,
});

/** Forgot-password form page. Exported for use under __main/$locale/forgot-password. */
export function ForgotPasswordPage() {
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof ForgotPasswordSchema>>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const forgotPassword = async (data: ForgotPasswordSchema) => {
    const { data: response, error } = await authClient.requestPasswordReset({
      email: data.email,
      redirectTo: withLocale("/reset-password"),
    });
    if (error) {
      throw createAuthError(
        error,
        "Beim Anfordern des Passwort-Resets ist ein Fehler aufgetreten. Bitte versuche es erneut.",
        "password-reset",
      );
    }
    return response;
  };

  const forgotPasswordMutation = useMutation({
    mutationFn: forgotPassword,
    onSuccess: () => {
      queryClient.resetQueries();
    },
  });
  const { isPending: isLoading, error, isSuccess } = forgotPasswordMutation;

  const handleSubmit = async (values: z.infer<typeof ForgotPasswordSchema>) => {
    try {
      await forgotPasswordMutation.mutateAsync(values);
      form.reset();
    } catch (err) {
      console.error("Caught mutation error:", err);
    }
  };

  return (
    <div className="flex flex-col items-center px-4 max-w-[480px] w-full mx-auto">
      <h1 className="mt-6 font-medium text-5xl">Passwort vergessen</h1>
      <div className="w-full lg:w-[480px]">
        {isSuccess && (
          <Alert className="mt-6">
            <CheckCircle />
            <AlertTitle>Anfrage erfolgreich</AlertTitle>
            <AlertDescription>
              Die Anfrage war erfolgreich. Wir haben dir eine E-Mail mit einem
              Link zum Zurücksetzen deines Passworts geschickt.
            </AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert className="mt-6" variant={"destructive"}>
            <TriangleAlert />
            <AlertTitle>Fehler bei der Anfrage</AlertTitle>
            <AlertDescription>
              {localizeAuthErrorMessage(error.message)}
            </AlertDescription>
          </Alert>
        )}
        {!isSuccess && (
          <div className="w-full lg:w-[480px]">
            <div className="mt-8 w-full">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="flex flex-col gap-3 w-full"
                  noValidate
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="mt-1">
                            <Input
                              {...field}
                              id="email"
                              name="email"
                              type="email"
                              maxLength={50}
                              placeholder="E-Mail"
                            />
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" variant="default">
                    {isLoading ? (
                      <>
                        <LoaderIcon className="animate-spin" />
                        Lädt...
                      </>
                    ) : (
                      "Absenden"
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
